import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { Plus, Pencil, Trash2, Package, Building2 } from 'lucide-react';
import {
  apiListWorkTypes, apiCreateWorkType, apiUpdateWorkType, apiDeleteWorkType,
  apiListCustomers
} from '../../api';
import { toast } from 'sonner';

const WorkTypeForm = ({ initial, customers, defaultCustomerId, onSubmit, onCancel }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? { ...initial } : {
    customer_id: defaultCustomerId || '', code: '', description: '',
    rate_eur_per_piece: 0, target_eur_per_hour: 0, active: true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isEdit && !form.customer_id) { toast.error('Seleziona il cliente'); return; }
    if (!form.code.trim() || !form.description.trim()) {
      toast.error('Codice e descrizione sono obbligatori');
      return;
    }
    const rate = parseFloat(form.rate_eur_per_piece);
    if (isNaN(rate) || rate < 0) { toast.error('Tariffa non valida'); return; }
    const payload = {
      ...form,
      rate_eur_per_piece: rate,
      target_eur_per_hour: parseFloat(form.target_eur_per_hour) || 0
    };
    // Don't send customer_id on update (not editable)
    if (isEdit) delete payload.customer_id;
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="worktype-form">
      {!isEdit && (
        <div className="space-y-2">
          <Label>Cliente <span className="text-red-500">*</span></Label>
          <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
            <SelectTrigger data-testid="worktype-customer-select"><SelectValue placeholder="Seleziona cliente..." /></SelectTrigger>
            <SelectContent>
              {customers.filter(c => c.active).map(c => (
                <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-2">
        <Label>Codice lavorazione <span className="text-red-500">*</span></Label>
        <Input
          data-testid="worktype-code-input"
          placeholder="es. CONF-01"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
          className="font-mono uppercase" required
        />
      </div>
      <div className="space-y-2">
        <Label>Descrizione <span className="text-red-500">*</span></Label>
        <Textarea
          data-testid="worktype-desc-input"
          rows={2} placeholder="Descrivi la lavorazione..."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          required className="resize-none"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Tariffa (€ / pezzo) <span className="text-red-500">*</span></Label>
          <Input
            data-testid="worktype-rate-input"
            type="number" step="0.001" min="0"
            value={form.rate_eur_per_piece}
            onChange={(e) => setForm({ ...form, rate_eur_per_piece: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Target (€/ora)</Label>
          <Input
            type="number" step="0.01" min="0"
            value={form.target_eur_per_hour}
            onChange={(e) => setForm({ ...form, target_eur_per_hour: e.target.value })}
          />
        </div>
      </div>
      <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
        <div className="text-sm font-medium">Lavorazione attiva</div>
        <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button data-testid="worktype-submit-btn" type="submit" className="bg-blue-700 hover:bg-blue-800">
          {isEdit ? 'Salva' : 'Crea lavorazione'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const WorkTypesManager = () => {
  const [customers, setCustomers] = useState([]);
  const [list, setList] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const reload = async () => {
    const [cs, wts] = await Promise.all([apiListCustomers(), apiListWorkTypes()]);
    setCustomers(cs || []);
    setList(wts || []);
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = list.filter(w => filterCustomer === 'all' || w.customer_id === filterCustomer);

  const handleSubmit = async (data) => {
    try {
      if (editing) {
        await apiUpdateWorkType(editing.id, data);
        toast.success('Lavorazione aggiornata');
      } else {
        await apiCreateWorkType(data);
        toast.success('Lavorazione creata');
      }
      setDialogOpen(false);
      setEditing(null);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeleteWorkType(toDelete.id);
      toast.success('Lavorazione eliminata');
      setToDelete(null);
      reload();
    } catch { toast.error('Errore eliminazione'); }
  };

  const customersById = customers.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="worktypes-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={filterCustomer} onValueChange={setFilterCustomer}>
            <SelectTrigger className="h-9 w-[260px] text-sm" data-testid="worktype-filter-customer">
              <SelectValue placeholder="Filtra per cliente..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button
              data-testid="worktype-new-btn"
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              className="bg-blue-700 hover:bg-blue-800 gap-2"
              disabled={customers.length === 0}
            >
              <Plus className="w-4 h-4" /> Nuova lavorazione
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifica lavorazione' : 'Nuova lavorazione'}</DialogTitle>
              <DialogDescription>
                {editing
                  ? `Cliente: ${customersById[editing.customer_id]?.name || '—'}`
                  : 'Ogni lavorazione è associata a un cliente con la sua tariffa specifica.'}
              </DialogDescription>
            </DialogHeader>
            <WorkTypeForm
              initial={editing}
              customers={customers}
              defaultCustomerId={filterCustomer !== 'all' ? filterCustomer : ''}
              onSubmit={handleSubmit}
              onCancel={() => { setDialogOpen(false); setEditing(null); }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessun cliente</div>
          <div className="text-xs text-slate-500 mt-1">Crea prima un cliente nella sezione "Clienti"</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessuna lavorazione</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Cliente</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Codice</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Descrizione</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">€ / pezzo</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Target €/ora</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Stato</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(w => {
                const cust = customersById[w.customer_id];
                return (
                  <tr key={w.id} className={`hover:bg-slate-50 ${!w.active ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="text-xs font-mono text-slate-500">{cust?.code || '—'}</div>
                      <div className="text-sm font-medium text-slate-800 truncate max-w-[160px]">{cust?.name || '—'}</div>
                    </td>
                    <td className="px-4 py-3 font-mono font-medium text-slate-900">{w.code}</td>
                    <td className="px-4 py-3 text-slate-700">{w.description}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">€{w.rate_eur_per_piece.toFixed(3)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">{w.target_eur_per_hour > 0 ? `€${w.target_eur_per_hour.toFixed(2)}` : '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${w.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                        {w.active ? 'Attiva' : 'Disattivata'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setEditing(w); setDialogOpen(true); }}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={() => setToDelete(w)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {toDelete?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              La lavorazione sarà rimossa dal catalogo del cliente. Le sessioni di produzione storiche rimarranno (la tariffa è salvata al momento della lettura).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkTypesManager;
